/**
 * Admin Log Page - System activity logs and audit trail
 * Displays user actions, system events, and administrative activities
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Badge,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
} from '@chakra-ui/react';
import {
  FiActivity,
  FiSearch,
  FiFilter,
  FiClock,
  FiUser,
  FiSettings,
  FiAlertTriangle,
  FiInfo,
  FiCheckCircle,
  FiX,
} from 'react-icons/fi';

import {
  AdminLayout,
  Card,
  StatsCard,
  DataTable,
  Button,
} from '@grcs/components';
import { useDatabase } from '../../hook/useDatabase';
import { LogCategory, LogSeverity } from '@grcs/utils/logging/logger';

interface LogEntry {
  id: string;
  timestamp: string;
  user_email: string;
  action: string;
  description: string;
  category: LogCategory;
  severity: LogSeverity;
  details?: string;
  ip_address?: string;
}

export const AdminLogPageEnhanced: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const db = useDatabase();

  // Fetch logs from database
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);

        // Query system_logs table
        const { data: logData, error: logError } = await db.user.supabaseClient
          .from('system_logs')
          .select('*')
          .order('timestamp', { ascending: false })
          .limit(100); // Limit to recent 100 logs

        if (logError) {
          console.error('Error fetching logs:', logError);
          if (
            logError.message?.includes('relation "system_logs" does not exist')
          ) {
            setError(
              'System logs table not found. Please run the database migration to create the system_logs table.',
            );
          } else {
            setError(`Failed to load system logs: ${logError.message}`);
          }
          setLogs([]);
        } else {
          // Transform data to match our interface
          const transformedLogs: LogEntry[] = (logData || []).map(
            (log: any) => ({
              id: log.id,
              timestamp: log.timestamp,
              user_email: log.user_email || 'System',
              action: log.action,
              description: log.description,
              category: log.category as LogCategory,
              severity: log.severity as LogSeverity,
              details: log.details,
              ip_address: log.ip_address,
            }),
          );
          setLogs(transformedLogs);
        }
      } catch (err) {
        console.error('Error in fetchLogs:', err);
        setError('Failed to load system logs');
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [db.user.supabaseClient]);

  // Filter logs based on search and filters
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchesSearch =
        log.user_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === 'all' || log.category === selectedCategory;
      const matchesSeverity =
        selectedSeverity === 'all' || log.severity === selectedSeverity;

      return matchesSearch && matchesCategory && matchesSeverity;
    });
  }, [logs, searchQuery, selectedCategory, selectedSeverity]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return FiX;
      case 'warning':
        return FiAlertTriangle;
      case 'success':
        return FiCheckCircle;
      default:
        return FiInfo;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'red';
      case 'warning':
        return 'orange';
      case 'success':
        return 'green';
      default:
        return 'blue';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'user':
        return FiUser;
      case 'system':
        return FiSettings;
      case 'homework':
        return FiActivity;
      case 'class':
        return FiActivity;
      default:
        return FiInfo;
    }
  };

  const logTableColumns = [
    {
      key: 'timestamp',
      header: 'Time',
      render: (item: LogEntry) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" fontWeight="medium">
            {item.timestamp.split(' ')[1]}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {item.timestamp.split(' ')[0]}
          </Text>
        </VStack>
      ),
    },
    {
      key: 'user_email',
      header: 'User/System',
      render: (item: LogEntry) => (
        <HStack spacing={2}>
          <Icon as={getCategoryIcon(item.category)} boxSize={4} />
          <Text fontSize="sm">{item.user_email}</Text>
        </HStack>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (item: LogEntry) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" fontWeight="medium">
            {item.action}
          </Text>
          <Text fontSize="xs" color="gray.600" noOfLines={2}>
            {item.description}
          </Text>
        </VStack>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (item: LogEntry) => (
        <Badge variant="subtle" size="sm" textTransform="capitalize">
          {item.category}
        </Badge>
      ),
    },
    {
      key: 'severity',
      header: 'Status',
      render: (item: LogEntry) => (
        <HStack spacing={2}>
          <Icon
            as={getSeverityIcon(item.severity)}
            boxSize={4}
            color={`${getSeverityColor(item.severity)}.500`}
          />
          <Badge
            colorScheme={getSeverityColor(item.severity)}
            variant="subtle"
            size="sm"
            textTransform="capitalize"
          >
            {item.severity}
          </Badge>
        </HStack>
      ),
    },
  ];

  const categoryOptions = [
    { label: 'All Categories', value: 'all' },
    { label: 'User Actions', value: 'user' },
    { label: 'System Events', value: 'system' },
    { label: 'Homework', value: 'homework' },
    { label: 'Classes', value: 'class' },
    { label: 'Authentication', value: 'auth' },
    { label: 'Files', value: 'file' },
  ];

  const severityOptions = [
    { label: 'All Levels', value: 'all' },
    { label: 'Info', value: 'info' },
    { label: 'Success', value: 'success' },
    { label: 'Warning', value: 'warning' },
    { label: 'Error', value: 'error' },
  ];

  const getStats = () => {
    const total = logs.length;
    const errors = logs.filter((log) => log.severity === 'error').length;
    const warnings = logs.filter((log) => log.severity === 'warning').length;
    const success = logs.filter((log) => log.severity === 'success').length;

    return { total, errors, warnings, success };
  };

  const stats = getStats();

  return (
    <AdminLayout
      header={{
        title: 'System Activity Logs',
        subtitle:
          'Monitor user actions, system events, and administrative activities',
        breadcrumb: {
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Logs', isCurrent: true },
          ],
        },
      }}
    >
      <VStack spacing={6} align="stretch">
        {/* Statistics Overview */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <StatsCard
            title="Total Events"
            value={stats.total.toString()}
            icon={FiActivity}
            trend="Last 24h"
          />
          <StatsCard
            title="Errors"
            value={stats.errors.toString()}
            icon={FiX}
            trend="Needs attention"
          />
          <StatsCard
            title="Warnings"
            value={stats.warnings.toString()}
            icon={FiAlertTriangle}
            trend="Monitor closely"
          />
          <StatsCard
            title="Success"
            value={stats.success.toString()}
            icon={FiCheckCircle}
            trend="All good"
          />
        </SimpleGrid>

        {/* Filters and Search */}
        <Card padding={4}>
          <HStack spacing={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement>
                <FiSearch />
              </InputLeftElement>
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Select
              maxW="200px"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              maxW="150px"
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
            >
              {severityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Button
              leftIcon={<FiFilter />}
              variant="secondary"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedSeverity('all');
              }}
            >
              Clear Filters
            </Button>
          </HStack>
        </Card>

        {/* Log Table */}
        <Card>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="lg" fontWeight="semibold">
                Activity Log
              </Text>
              <Text fontSize="sm" color="gray.500">
                Showing {filteredLogs.length} of {logs.length} entries
              </Text>
            </HStack>

            {loading ? (
              <Text>Loading system logs...</Text>
            ) : error ? (
              <VStack
                spacing={3}
                p={4}
                bg="red.50"
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
              >
                <Text color="red.600" fontWeight="medium">
                  System Logs Error
                </Text>
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {error}
                </Text>
                {error.includes('table not found') && (
                  <VStack spacing={2} fontSize="sm" color="gray.600">
                    <Text fontWeight="medium">To fix this issue:</Text>
                    <Text>1. Go to your Supabase SQL Editor</Text>
                    <Text>
                      2. Run the SQL from /supabase/seed.sql to create the
                      system_logs table
                    </Text>
                    <Text>3. Refresh this page</Text>
                  </VStack>
                )}
              </VStack>
            ) : (
              <DataTable
                data={filteredLogs}
                columns={logTableColumns}
                searchable={false}
              />
            )}
          </VStack>
        </Card>

        {/* Log Details/Recent Activity */}
        <Card padding={6} variant="filled">
          <VStack spacing={4} align="start">
            <HStack spacing={3}>
              <Icon as={FiClock} boxSize={5} color="blue.500" />
              <Text fontSize="lg" fontWeight="semibold">
                Log Information
              </Text>
            </HStack>
            <VStack spacing={2} align="start" fontSize="sm" color="gray.600">
              <Text>
                • System logs are automatically generated for all user actions
                and system events
              </Text>
              <Text>
                • Logs are retained for 90 days and archived for compliance
                purposes
              </Text>
              <Text>
                • Error logs trigger automatic notifications to system
                administrators
              </Text>
              <Text>
                • All sensitive data is masked in log entries for privacy
                protection
              </Text>
            </VStack>
          </VStack>
        </Card>
      </VStack>
    </AdminLayout>
  );
};
